/** 常量定义 — 侧边栏配置、状态枚举映射、颜色映射。
 *
 * 状态色全部引用 theme palette（theme.ts 为唯一色值源）；
 * 徽章浅底取自 palette.semanticBg.*（对齐 theme.css --p0-bg/--p1-bg/--ok-bg/--info-bg/--neutral-bg）。
 */
import { theme } from "../theme/theme";

/** 侧边栏分组配置。 */
export interface NavItem {
  label: string;
  path: string;
  /**
   * 平台级菜单标记（二期批1 运营方 UI 差异）：true 时仅 role=admin 可见，
   * 运营方/企业客户登录不渲染该入口（Layout 侧边栏按此过滤）。
   */
  adminOnly?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const SIDEBAR_GROUPS: NavGroup[] = [
  {
    title: "总览",
    items: [{ label: "情报总览", path: "/" }],
  },
  {
    title: "资产管理",
    items: [
      { label: "公司", path: "/companies" },
      { label: "品牌", path: "/brands" },
      { label: "产品", path: "/products" },
      { label: "媒体账户", path: "/owned-accounts" },
    ],
  },
  {
    title: "GEO 分析",
    items: [
      { label: "AI 可见性", path: "/ai-visibility" },
      { label: "竞品对比", path: "/competitive" },
      { label: "引用源追溯", path: "/citation-sources" },
      { label: "收录台账", path: "/index-ledger" },
    ],
  },
  {
    title: "媒体投放",
    items: [
      { label: "媒体总库", path: "/media-pool" },
      { label: "媒体投放库", path: "/media-insight" },
      { label: "文案库", path: "/copy-library" },
      { label: "生文管理", path: "/generation" },
      { label: "投放计划", path: "/delivery-plans" },
      { label: "费用统计", path: "/expenses" },
      { label: "发文记录", path: "/publish-records" },
    ],
  },
  {
    title: "系统",
    // 平台级菜单（二期批1）：仅 admin 可见，运营方/客户不展示
    items: [
      { label: "平台账户", path: "/platform-account", adminOnly: true },
      { label: "留资管理", path: "/leads", adminOnly: true },
      { label: "系统设置", path: "/system-settings", adminOnly: true },
    ],
  },
];

/** 徽章配色（深字 + 浅底），取自 theme 语义色。 */
const BADGE = {
  success: { color: theme.palette.success.main, bg: theme.palette.semanticBg.success },
  warning: { color: theme.palette.warning.main, bg: theme.palette.semanticBg.warning },
  error: { color: theme.palette.error.main, bg: theme.palette.semanticBg.error },
  info: { color: theme.palette.info.main, bg: theme.palette.semanticBg.info },
  neutral: { color: theme.palette.text.secondary, bg: theme.palette.semanticBg.neutral },
} as const;

/** 投放计划状态映射。 */
export const DELIVERY_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending_confirm: { label: "待确认", ...BADGE.warning },
  confirmed: { label: "已确认", ...BADGE.info },
  delivered: { label: "已投放", ...BADGE.success },
  cancelled: { label: "已取消", ...BADGE.error },
};

/** 认证状态映射。 */
export const AUTH_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  verified: { label: "已认证", ...BADGE.success },
  pending: { label: "待认证", ...BADGE.warning },
  expired: { label: "已过期", ...BADGE.error },
  needs_update: { label: "需更新", ...BADGE.warning },
};

/** 发文状态映射。 */
export const PUBLISH_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  published: { label: "已发布", ...BADGE.success },
  scheduled: { label: "已排期", ...BADGE.info },
  draft: { label: "草稿", ...BADGE.neutral },
};

/** 文案状态映射。 */
export const COPY_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending_confirm: { label: "待确认", ...BADGE.warning },
  confirmed: { label: "已确认", ...BADGE.info },
  used: { label: "已投放", ...BADGE.success },
  archived: { label: "已归档", ...BADGE.neutral },
  draft: { label: "草稿", ...BADGE.neutral },
};

/** 媒体投放库状态映射（active 启用 / disabled 停用）。 */
export const MEDIA_DELIVERY_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: "启用", ...BADGE.success },
  disabled: { label: "停用", ...BADGE.neutral },
};

/** 媒体投放来源映射（auto 信息源匹配 / manual 手动添加）。 */
export const MEDIA_ADD_SOURCE_MAP: Record<string, { label: string; color: string; bg: string }> = {
  auto: { label: "信息源匹配", ...BADGE.info },
  manual: { label: "手动添加", ...BADGE.neutral },
};

/** 媒体等级映射（置顶/一级/二级/三级/四级/其他）。 */
export const MEDIA_TIER_TEXT: Record<string, string> = {
  "置顶媒体": "置顶",
  "一级媒体": "一级",
  "二级媒体": "二级",
  "三级媒体": "三级",
  "四级媒体": "四级",
};

/** 媒体等级 → 徽章色（置顶/一级高亮，二级信息，其他灰）。 */
function mediaTierBadge(tier?: string | null) {
  if (!tier) return { label: "—", color: "text.secondary", bg: "semanticBg.neutral" };
  if (tier.includes("置顶")) return { label: "置顶", ...BADGE.warning };
  if (tier.includes("一级")) return { label: "一级", ...BADGE.warning };
  if (tier.includes("二级")) return { label: "二级", ...BADGE.info };
  if (tier.includes("三级")) return { label: "三级", ...BADGE.success };
  if (tier.includes("四级")) return { label: "四级", ...BADGE.neutral };
  return { label: tier, color: "text.secondary", bg: "semanticBg.neutral" };
}

/** 媒体等级标签（未命中回退原值）。 */
export function mediaTierLabel(tier?: string | null): string {
  if (!tier) return "—";
  return MEDIA_TIER_TEXT[tier] || tier;
}

/** 媒体等级徽章配置（供 TierBadge 复用）。 */
export function mediaTierBadgeConfig(tier?: string | null) {
  return mediaTierBadge(tier);
}

/** GHS 六维名称映射。 */
export const GHS_DIMENSIONS: { key: string; label: string; weight: number }[] = [
  { key: "schema_coverage", label: "Schema", weight: 15 },
  { key: "content_quality", label: "内容", weight: 30 },
  { key: "tech_basics", label: "技术", weight: 15 },
  { key: "compliance", label: "合规", weight: 10 },
  { key: "ai_visibility", label: "AI可见性", weight: 20 },
  { key: "easy_fix", label: "易修复", weight: 10 },
];

/** GEO 综合分六维（指标字典 §13.2，与网站质量分相互独立、不合成总分）。
 *  desc 为该维「是什么」的直读解释（PRD 附录 B2）。 */
export const GEO_SCORE_DIMENSIONS: { key: string; label: string; desc: string }[] = [
  { key: "scene_coverage", label: "场景覆盖", desc: "本轮有品牌提及的场景占监测场景的比例" },
  { key: "brand_mention", label: "品牌提及", desc: "本轮探测样本中提及品牌的比例" },
  { key: "first_recommendation", label: "首推强度", desc: "本轮 AI 回答中品牌被首推的比例" },
  { key: "citation_trust", label: "引用采信", desc: "AI 引用中自有资产引用的占比" },
  { key: "sentiment", label: "情感倾向", desc: "AI 回答对品牌情感的净正向程度" },
  { key: "competitive_position", label: "竞争位次", desc: "品牌在同类声量中的份额（SoV）" },
];

/** 意图映射。 */
export const INTENT_MAP: Record<string, string> = {
  选购: "认知阶段",
  对比: "决策阶段",
  参数: "调研阶段",
  使用场景: "体验阶段",
  售后: "售后阶段",
};

/**
 * LLM/AI 平台 slug → 中文名（展示层翻译）。
 * 后端返回值为 slug（doubao/qwen/...），展示一律查此表，未命中回退原值。
 */
export const PLATFORM_MAP: Record<string, string> = {
  doubao: "豆包",
  deepseek: "DeepSeek",
  qwen: "通义千问",
  qianwen: "千问",
  yuanbao: "元宝",
  wenxin: "文心一言",
  kimi: "Kimi",
};

/** 平台 slug → 中文名（未命中回退原值）。 */
export function platformLabel(slug: string | null | undefined): string {
  if (!slug) return "—";
  return PLATFORM_MAP[slug] || slug;
}

/**
 * 热力色阶（5 级，对齐 theme.css §1.3 --l0..--l4 / --l0-t..--l4-t）。
 * 例外说明：热力格需真实 hex 做背景填充与文字色，无对应 theme palette token，
 * 按"色阶表"例外集中定义于此（对齐 theme.css §1.3），组件内禁止再散落硬编码。
 */
export const HEAT_LEVELS = [
  { bg: "#F3F4F6", text: "#9CA3AF" }, // l0 无数据
  { bg: "#FEE2E2", text: "#B91C1C" }, // l1 差（0–20）
  { bg: "#FEF3C7", text: "#B45309" }, // l2 中（21–50）
  { bg: "#D1FAE5", text: "#047857" }, // l3 良（51–80）
  { bg: "#059669", text: "#FFFFFF" }, // l4 优（81–100）
] as const;

/** 分值 → 热力色阶档位（null/undefined → l0 无数据）。 */
export function heatLevel(score: number | null | undefined): 0 | 1 | 2 | 3 | 4 {
  if (score === null || score === undefined || Number.isNaN(score)) return 0;
  if (score <= 20) return 1;
  if (score <= 50) return 2;
  if (score <= 80) return 3;
  return 4;
}

/**
 * 内容类型 slug → 中文名（展示层翻译）。
 */
export const CONTENT_TYPE_MAP: Record<string, string> = {
  product: "产品稿",
  scene: "场景稿",
  qa: "问答稿",
  reputation: "口碑稿",
  news: "资讯稿",
  tutorial: "教程稿",
};

/** 内容类型 slug → 中文名（未命中回退原值）。 */
export function contentTypeLabel(slug: string | null | undefined): string {
  if (!slug) return "—";
  return CONTENT_TYPE_MAP[slug] || slug;
}

/**
 * 信源类型 slug → 中文名（展示层翻译）。
 */
export const SOURCE_TYPE_MAP: Record<string, string> = {
  official_website: "官网",
  news_media: "新闻媒体",
  social_media: "社交媒体",
  forum: "论坛社区",
  video: "视频平台",
  ecommerce: "电商平台",
  other: "其他",
};

/** 信源类型 slug → 中文名（未命中回退原值）。 */
export function sourceTypeLabel(slug: string | null | undefined): string {
  if (!slug) return "—";
  return SOURCE_TYPE_MAP[slug] || slug;
}

/** 优先级映射。 */
export const PRIORITY_MAP: Record<string, { label: string; color: string; bg: string }> = {
  P0: { label: "P0", ...BADGE.error },
  P1: { label: "P1", ...BADGE.warning },
  P2: { label: "P2", ...BADGE.info },
};

/** 权重档位映射（high/mid/low → 高/中/低；高 warning / 中 info / 低 neutral）。 */
export const TIER_MAP: Record<string, { label: string; color: string; bg: string }> = {
  high: { label: "高", ...BADGE.warning },
  mid: { label: "中", ...BADGE.info },
  low: { label: "低", ...BADGE.neutral },
};

/** 收录台账 URL 状态映射（indexed 成功 / pending 信息 / abnormal 错误 / 其他灰）。 */
export const LEDGER_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  indexed: { label: "已收录", ...BADGE.success },
  pending: { label: "复检中", ...BADGE.info },
  abnormal: { label: "未收录", ...BADGE.error },
  resolved: { label: "已处理", ...BADGE.neutral },
  demoted: { label: "降频周检", ...BADGE.neutral },
};

/** 事实包版本状态映射。 */
export const FACT_PACK_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  finalized: { label: "定稿", ...BADGE.success },
  pending_review: { label: "待复审", ...BADGE.warning },
  in_review: { label: "会签中", ...BADGE.info },
  draft: { label: "草稿", ...BADGE.neutral },
};

/** 事实条目级别映射（A 成功 / B 信息）。 */
export const FACT_LEVEL_MAP: Record<string, { label: string; color: string; bg: string }> = {
  A: { label: "A 级", ...BADGE.success },
  B: { label: "B 级", ...BADGE.info },
};

/** 事实条目授权状态映射（可署名 成功 / 匿名 信息 / 仅内部 错误）。 */
export const FACT_AUTH_MAP: Record<string, { label: string; color: string; bg: string }> = {
  named: { label: "可署名", ...BADGE.success },
  anonymous: { label: "匿名", ...BADGE.info },
  internal: { label: "仅内部", ...BADGE.error },
};

/**
 * 引用频次颜色映射（媒体洞察旧 IA 遗留常量，批 5 重写后由 TIER_MAP 接管档位展示；
 * 保留供后续频次维度复用）。
 */
export const FREQ_COLORS: Record<string, { color: string; bg: string }> = {
  高: { color: theme.palette.error.main, bg: theme.palette.semanticBg.error },
  中: { color: theme.palette.warning.main, bg: theme.palette.semanticBg.warning },
  低: { color: theme.palette.text.secondary, bg: theme.palette.semanticBg.neutral },
};

/**
 * LLM 平台标识色（SystemSettings 配置卡用，需真实 hex 以支持 alpha 后缀拼色）。
 * 例外说明：yuanbao #07C160（微信绿）/ wenxin #2932E1（文心蓝）为第三方平台品牌色，
 * 无对应 theme token，按"纯展示色"例外集中保留于此；其余引用 theme palette。
 */
export const PLATFORM_COLORS: Record<string, string> = {
  doubao: theme.palette.primary.main,
  deepseek: theme.palette.info.main,
  qwen: theme.palette.warning.main,
  yuanbao: "#07C160", // 第三方平台品牌色（例外，见上方注释）
  wenxin: "#2932E1", // 第三方平台品牌色（例外，见上方注释）
};

/**
 * 五维词库维度配置（MaterialLibrary 用，需真实 hex 以支持 alpha 后缀拼色）。
 * 分类展示色引用 theme palette 主色（品牌/信息/成功/警告/错误 五色区分维度）。
 */
export const WORD_LIBRARY_DIMENSIONS: { key: string; label: string; color: string }[] = [
  { key: "user", label: "用户维度", color: theme.palette.primary.main },
  { key: "scene", label: "场景维度", color: theme.palette.info.main },
  { key: "product", label: "产品维度", color: theme.palette.success.main },
  { key: "selling_point", label: "卖点维度", color: theme.palette.warning.main },
  { key: "evidence", label: "证据维度", color: theme.palette.error.main },
];
